import { useEffect, useRef, useState } from 'react';
import {
  CustomAttributeValue,
  NormalizedCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';

const SAVE_DEBOUNCE_MS = 300;

interface UseDebouncedAttributeSaveParams {
  definition: NormalizedCustomAttributeDefinition;
  /** The value the server currently reports for this attribute. */
  serverValue: CustomAttributeValue;
  isSaving: boolean;
  onSave: (
    definition: NormalizedCustomAttributeDefinition,
    value: CustomAttributeValue,
  ) => void;
}

/**
 * Holds the user's latest intended value and fires the API call only once they
 * stop interacting, rendering that value optimistically in the meantime.
 */
export const useDebouncedAttributeSave = ({
  definition,
  serverValue,
  isSaving,
  onSave,
}: UseDebouncedAttributeSaveParams) => {
  const [pendingValue, setPendingValue] = useState<CustomAttributeValue | null>(null);
  const debouncedPending = useDebouncedValue(pendingValue, SAVE_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedPending !== null) {
      onSave(definition, debouncedPending);
    }
    // onSave/definition intentionally omitted — we only want to react to debounced changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPending]);

  // A finished save may only release the value it actually carried. A newer
  // pending value still inside the debounce has not been sent: dropping it here
  // would both revert the row to the pre-edit value and cancel that save.
  const isPendingDispatched = debouncedPending === pendingValue;
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving && isPendingDispatched) {
      setPendingValue(null);
    }
    wasSavingRef.current = isSaving;
  }, [isSaving, isPendingDispatched]);

  return { displayedValue: pendingValue ?? serverValue, submit: setPendingValue };
};
